import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ServerCrash, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { CardSkeleton } from "@/components/loading";
import { useSystemLogsQuery } from "@/hooks/use-system-logs";
import type { SystemLogListItem } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { SystemLogCard } from "@/components/admin/system-logs/SystemLogCard";
import {
  SystemLogFilters,
  type SystemLogFiltersValue,
} from "@/components/admin/system-logs/SystemLogFilters";
import { SystemLogSearch } from "@/components/admin/system-logs/SystemLogSearch";
import { SystemLogDetailDrawer } from "@/components/admin/system-logs/SystemLogDetailDrawer";

export const Route = createFileRoute("/_authenticated/admin/system-logs")({
  component: AdminSystemLogsPage,
});

const PAGE_SIZE = 20;

/**
 * System Logs — internal system health (exceptions, warnings, background-job
 * failures, auth failures). Kept entirely separate from the Audit Logs page
 * (user activity trail); this endpoint is SystemAdmin-only server-side, so
 * access here is gated purely on `isAdmin` (no Teamleader visibility at all).
 */
function AdminSystemLogsPage() {
  const { isAdmin } = useAuth();

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<SystemLogFiltersValue>({});
  const [page, setPage] = useState(1);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

  const hasActiveFilters =
    !!search || Object.values(filters).some((v) => v !== undefined && v !== "");

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function updateFilters(patch: Partial<SystemLogFiltersValue>) {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }

  function clearAll() {
    setSearch("");
    setFilters({});
    setPage(1);
  }

  const query = useMemo(
    () => ({
      ...filters,
      search: search.trim() || undefined,
      page,
      pageSize: PAGE_SIZE,
      sortBy: "timestamp",
      sortDesc: true,
    }),
    [filters, search, page],
  );

  const { data, isLoading, isFetching, refetch } = useSystemLogsQuery(query);

  const logs: SystemLogListItem[] = data?.items ?? [];

  if (!isAdmin) {
    return <AccessDenied message="You don't have permission to view system logs." />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-4 pb-2 border-b border-muted">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10 text-primary shadow-inner">
              <ServerCrash className="h-7 w-7" />
            </div>
            System Logs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Internal system health — exceptions, warnings, background-job failures, and
            authentication failures.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2 hover:bg-muted/50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            {isFetching ? "Loading…" : "Refresh"}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-wrap items-center gap-3">
        <SystemLogSearch value={search} onChange={handleSearchChange} />
      </div>

      {/* Filters */}
      <SystemLogFilters
        value={filters}
        onChange={updateFilters}
        onClear={clearAll}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Result summary */}
      <p className="text-xs text-muted-foreground">
        {isLoading
          ? "Loading system logs…"
          : `Showing ${logs.length} of ${data?.totalCount ?? 0} log entries`}
      </p>

      {/* Content */}
      {isLoading ? (
        <CardSkeleton count={6} className="grid-cols-1 sm:grid-cols-1 lg:grid-cols-1" />
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border rounded-2xl bg-card">
          <ServerCrash className="h-10 w-10 mx-auto mb-3 opacity-30 text-primary" />
          <p className="text-sm font-medium">No system log entries match the current filters.</p>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" className="mt-4 gap-2" onClick={clearAll}>
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <SystemLogCard key={log.id} log={log} onSelect={(l) => setSelectedLogId(l.id)} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (data.hasPreviousPage) setPage((p) => Math.max(1, p - 1));
                }}
                className={!data.hasPreviousPage ? "pointer-events-none opacity-40" : undefined}
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#" isActive className="pointer-events-none">
                {data.pageNumber} / {data.totalPages}
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (data.hasNextPage) setPage((p) => p + 1);
                }}
                className={!data.hasNextPage ? "pointer-events-none opacity-40" : undefined}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <SystemLogDetailDrawer logId={selectedLogId} onClose={() => setSelectedLogId(null)} />
    </div>
  );
}
